import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Chip,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import { normalizeColOptions } from "app/websocketManager/utils";
import { getEditorByColumnType, parseFiles } from "../../views/table/columnEditors/ReusableColumnEditorsFactory/ColumnEditorFactory";
import { getColumnLabel, getEnglishColumnLabel } from "./GFTranslations";
import { GF_FORMULA_COLUMN_IDS } from "./GFFormSections";
import GFSubsheetForm from "./GFSubsheetForm";
import tinycolor from "tinycolor2";

function withNormalized(column) {
  if (!column) return column;
  if (column.normalizedColumnOptions) return column;
  return { ...column, normalizedColumnOptions: normalizeColOptions(column.options) };
}

function getDropdownChips(value, normalizedColumnOptions) {
  if (!value) return null;
  const values = String(value).split(",").map((v) => v.trim()).filter(Boolean);
  const options = normalizedColumnOptions?.options ?? [];
  const colors = normalizedColumnOptions?.colors ?? [];

  return values.map((val) => {
    const idx = options.indexOf(val);
    const bg = idx >= 0 && colors[idx] ? colors[idx] : "#e0e0e0";
    const luminance = tinycolor(bg).getLuminance();
    const textColor = luminance > 0.5 ? "#222" : "#fff";
    return (
      <Chip
        key={val}
        label={val}
        size="small"
        style={{ background: bg, color: textColor, margin: "2px", fontSize: 12 }}
      />
    );
  });
}

function FieldDisplay({ column, value, activeLanguage }) {
  const typeId = String(column?.column_type_id ?? "1");
  const normalizedOpts = column?.normalizedColumnOptions ?? normalizeColOptions(column?.options);

  if (!value && value !== 0) {
    return (
      <Typography variant="body2" style={{ color: "rgba(0,0,0,0.35)", fontStyle: "italic" }}>
        Click to edit...
      </Typography>
    );
  }

  if (["3", "11", "13"].includes(typeId)) {
    const chips = getDropdownChips(value, normalizedOpts);
    return (
      <Box display="flex" flexWrap="wrap">
        {chips?.length ? chips : (
          <Typography variant="body2" style={{ color: "rgba(0,0,0,0.35)", fontStyle: "italic" }}>
            Click to edit...
          </Typography>
        )}
      </Box>
    );
  }

  if (typeId === "12") {
    const isChecked = value === "1" || value === true || value === 1;
    return (
      <Chip
        label={isChecked ? "Yes" : "No"}
        size="small"
        style={{
          background: isChecked ? "#4caf50" : "#e0e0e0",
          color: isChecked ? "#fff" : "#555",
          fontSize: 12,
        }}
      />
    );
  }

  if (typeId === "9") {
    const files = parseFiles(value);
    if (!files.length) return (
      <Typography variant="body2" style={{ color: "rgba(0,0,0,0.35)", fontStyle: "italic" }}>
        Click to upload...
      </Typography>
    );
    return (
      <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
        {files.map((f, i) => (
          <Chip key={i} label={f?.name || `File ${i + 1}`} size="small" icon={<span>📎</span>} />
        ))}
      </Box>
    );
  }

  if (typeId === "10" || typeId === "10-1") {
    const display = value?.value || (typeof value === "string" ? value : "") || "";
    return <Typography variant="body2">{display || "—"}</Typography>;
  }

  return <Typography variant="body2" style={{ wordBreak: "break-word" }}>{String(value)}</Typography>;
}

function GFFormFieldRenderer({ column, value, onChange, activeLanguage, error, store, data, isFullWidth, subColumns }) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const normalizedColumn = withNormalized(column);
  const columnId = String(column?.id ?? "");
  const isReadOnly = GF_FORMULA_COLUMN_IDS.has(columnId);
  const label = getColumnLabel(column, activeLanguage);
  const englishLabel = (activeLanguage && activeLanguage !== "English")
    ? getEnglishColumnLabel(column)
    : null;
  const typeId = String(column?.column_type_id ?? "1");
  const isMandatory = (() => {
    const opts = normalizedColumn?.normalizedColumnOptions;
    return opts?.is_mandatory === true || opts?.is_mandatory === "1" || opts?.is_mandatory === 1;
  })();

  const handleSaveValue = useCallback(async (newValue) => {
    onChange(newValue);
    setEditorOpen(false);
  }, [onChange]);

  if (typeId === "6") {
    return (
      <GFSubsheetForm
        column={normalizedColumn}
        subColumns={subColumns ?? []}
        value={value}
        onChange={onChange}
        activeLanguage={activeLanguage}
        error={error}
        store={store}
        data={data}
      />
    );
  }

  const editorProps = {
    column: normalizedColumn,
    editorValue: value,
    editorOpen,
    editorColKey: label,
    data,
    store,
    setEditorOpen,
    setLoading,
    bodyRef: { current: null },
    handleSaveValue,
    params: {
      listId: store?.listConfig?.list?.id,
      api: store?.gridApi || store?.currentViewStore?.gridApi,
    },
  };

  if (isReadOnly) {
    return (
      <Box>
        <Typography variant="caption" style={{ color: "rgba(0,0,0,0.54)", display: "block", marginBottom: 2 }}>
          {englishLabel && (
            <span style={{ display: "block", color: "rgba(0,0,0,0.38)", fontSize: 10, fontWeight: 400, marginBottom: 1 }}>
              {englishLabel}
            </span>
          )}
          {label} <span style={{ fontSize: 10, background: "#e3f2fd", color: "#1565c0", padding: "1px 5px", borderRadius: 8, marginLeft: 4 }}>Auto-computed</span>
        </Typography>
        <Box
          p={1}
          style={{
            border: "1px solid rgba(0,0,0,0.12)",
            borderRadius: 4,
            background: "#f5f5f5",
            minHeight: 36,
          }}
        >
          <Typography variant="body2" style={{ color: "#9e9e9e", fontStyle: "italic" }}>
            {value || "—"}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box style={isFullWidth ? { gridColumn: "1 / -1" } : {}}>
      <Box style={{ marginBottom: 3 }}>
        {englishLabel && (
          <Typography
            variant="caption"
            style={{
              display: "block",
              color: "rgba(0,0,0,0.38)",
              fontSize: 10,
              fontWeight: 400,
              lineHeight: 1.3,
              marginBottom: 1,
            }}
          >
            {englishLabel}
            {isMandatory && <span style={{ color: "#f44336", marginLeft: 2 }}>*</span>}
          </Typography>
        )}
        <Typography
          variant="caption"
          style={{
            color: error ? "#f44336" : "rgba(0,0,0,0.54)",
            display: "block",
            fontWeight: 600,
          }}
        >
          {label}
          {isMandatory && !englishLabel && <span style={{ color: "#f44336", marginLeft: 2 }}>*</span>}
        </Typography>
      </Box>

      <Box
        onClick={() => !isReadOnly && setEditorOpen(true)}
        style={{
          border: `1px solid ${error ? "#f44336" : "rgba(0,0,0,0.23)"}`,
          borderRadius: 4,
          padding: "8px 10px",
          minHeight: 40,
          cursor: "pointer",
          background: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          transition: "border-color 0.15s",
          "&:hover": { borderColor: "rgba(0,0,0,0.87)" },
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(0,0,0,0.87)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = error ? "#f44336" : "rgba(0,0,0,0.23)"; }}
      >
        <Box flex={1} overflow="hidden">
          <FieldDisplay column={normalizedColumn} value={value} activeLanguage={activeLanguage} />
        </Box>
        <EditIcon style={{ fontSize: 14, color: "rgba(0,0,0,0.3)", flexShrink: 0 }} />
      </Box>

      {error && (
        <Typography variant="caption" style={{ color: "#f44336", marginTop: 2, display: "block" }}>
          {error}
        </Typography>
      )}

      {editorOpen && getEditorByColumnType(editorProps)}
    </Box>
  );
}

export default GFFormFieldRenderer;
